import { useState, useCallback, useEffect } from 'react';
import { recordCorrect, recordIncorrect } from '../../../hooks/useQuizMastery';
import { recordCorrectDB, recordIncorrectDB, toggleBookmarkDB } from '../../../hooks/useQuizMasteryDB';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
  question: string;
  answer: string;
  choices: string[];
  explanation: string | null;
}

interface QuizPlayProps {
  questions: QuizQuestion[];
  onClose: () => void;
  onFinished?: () => void;
  onRetryIncorrect?: (incorrectQuestions: QuizQuestion[]) => void;
  isLoggedIn?: boolean;
  initialBookmarkedQuestions?: Set<string>;
  onRequestLogin?: () => void;
}

// セッション保存用の型定義
interface QuizSession {
  questionIds: string;
  currentIndex: number;
  results: ('correct' | 'incorrect')[];
  selectedChoice: number | null;
  answered: boolean;
  bookmarkedQuestions: string[];
  timestamp: number;
}

// 問題セットのハッシュを生成
const generateQuestionHash = (questions: QuizQuestion[]): string => {
  return questions.map(q => q.id).join(',');
};

// セッションストレージのキー
const SESSION_KEY = 'quiz_play_session';

export default function QuizPlay({
  questions,
  onClose,
  onFinished,
  onRetryIncorrect,
  isLoggedIn,
  initialBookmarkedQuestions,
  onRequestLogin,
}: QuizPlayProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [results, setResults] = useState<('correct' | 'incorrect')[]>([]);
  const [finished, setFinished] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [countUpValue, setCountUpValue] = useState(0);
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState<Set<string>>(
    initialBookmarkedQuestions || new Set()
  );
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedSession, setSavedSession] = useState<QuizSession | null>(null);

  const current = questions[currentIndex];
  const total = questions.length;
  const choiceLabels = ['A', 'B', 'C', 'D'];

  const correctCount = results.filter((r) => r === 'correct').length;
  const incorrectCount = results.filter((r) => r === 'incorrect').length;

  const incorrectQuestions = questions.filter((_, i) => results[i] === 'incorrect');

  // セッションをクリア
  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
  }, []);

  // セッションを保存
  const saveSession = useCallback(() => {
    if (finished) return;
    
    const session: QuizSession = {
      questionIds: generateQuestionHash(questions),
      currentIndex,
      results,
      selectedChoice,
      answered,
      bookmarkedQuestions: Array.from(bookmarkedQuestions),
      timestamp: Date.now(),
    };
    
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }, [questions, currentIndex, results, selectedChoice, answered, bookmarkedQuestions, finished]);

  // 初回マウント時にセッションを復元
  useEffect(() => {
    const savedData = sessionStorage.getItem(SESSION_KEY);
    if (!savedData) return;

    try {
      const session: QuizSession = JSON.parse(savedData);
      const currentHash = generateQuestionHash(questions);
      
      // 問題セットが同じで、24時間以内のセッションのみ復元対象
      if (
        session.questionIds === currentHash &&
        Date.now() - session.timestamp < 24 * 60 * 60 * 1000 &&
        session.currentIndex > 0
      ) {
        setSavedSession(session);
        setShowResumePrompt(true);
      } else {
        // 古いセッションや異なる問題セットの場合はクリア
        clearSession();
      }
    } catch (error) {
      console.error('Failed to restore session:', error);
      clearSession();
    }
  }, [questions, clearSession]);

  // セッションを復元
  const restoreSession = useCallback(() => {
    if (!savedSession) return;

    setCurrentIndex(savedSession.currentIndex);
    setResults(savedSession.results);
    setSelectedChoice(savedSession.selectedChoice);
    setAnswered(savedSession.answered);
    setBookmarkedQuestions(new Set(savedSession.bookmarkedQuestions));
    setShowResumePrompt(false);
    setSavedSession(null);
  }, [savedSession]);

  // 最初からやり直す
  const startFromBeginning = useCallback(() => {
    clearSession();
    setShowResumePrompt(false);
    setSavedSession(null);
  }, [clearSession]);

  // 状態が変わるたびにセッションを保存
  useEffect(() => {
    if (!showResumePrompt && !finished) {
      saveSession();
    }
  }, [currentIndex, results, selectedChoice, answered, bookmarkedQuestions, showResumePrompt, finished, saveSession]);

  // Keyboard shortcuts for quiz
  useKeyboardShortcuts({
    onQuizAnswer: (choiceIndex) => {
      if (!finished && !answered && current && current.choices && current.choices.length === 4) {
        handleSelectChoice(choiceIndex);
      }
    },
    onQuizNext: () => {
      if (!finished && answered) {
        handleNext();
      } else if (!finished && !answered && selectedChoice !== null && current && current.choices && current.choices.length === 4) {
        handleConfirm();
      }
    },
    onQuizExit: () => {
      if (!finished) {
        handleClose();
      }
    },
    disabled: finished,
  });

  // Initialize bookmarked questions - only from initialBookmarkedQuestions (DB-based)
  useEffect(() => {
    if (initialBookmarkedQuestions) {
      setBookmarkedQuestions(initialBookmarkedQuestions);
    } else {
      setBookmarkedQuestions(new Set());
    }
  }, [initialBookmarkedQuestions]);

  const doRecordCorrect = useCallback(
    (qId: string) => {
      if (isLoggedIn) {
        recordCorrectDB(qId);
      }
      recordCorrect(qId);
    },
    [isLoggedIn]
  );

  const doRecordIncorrect = useCallback(
    (qId: string) => {
      if (isLoggedIn) {
        recordIncorrectDB(qId);
      }
      recordIncorrect(qId);
    },
    [isLoggedIn]
  );

  const handleToggleBookmark = async (qId: string) => {
    if (!isLoggedIn) {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
      if (onRequestLogin) {
        setTimeout(() => onRequestLogin(), 500);
      }
      return;
    }

    const currentStatus = bookmarkedQuestions.has(qId);
    const newStatus = await toggleBookmarkDB(qId, currentStatus);
    setBookmarkedQuestions((prev) => {
      const next = new Set(prev);
      if (newStatus) next.add(qId);
      else next.delete(qId);
      return next;
    });
  };

  useEffect(() => {
    if (finished && total > 0) {
      // クイズ完了時にセッションをクリア
      clearSession();
      
      setShowConfetti(true);
      const target = Math.round((correctCount / total) * 100);
      let cur = 0;
      const interval = setInterval(() => {
        cur += 1;
        if (cur > target) {
          clearInterval(interval);
          setCountUpValue(target);
        } else {
          setCountUpValue(cur);
        }
      }, 15);
      const confettiTimer = setTimeout(() => setShowConfetti(false), 3000);
      return () => {
        clearInterval(interval);
        clearTimeout(confettiTimer);
      };
    }
  }, [finished, correctCount, total, clearSession]);

  const handleSelectChoice = (idx: number) => {
    if (answered) return;
    setSelectedChoice(idx);
  };

  const handleConfirm = useCallback(() => {
    if (selectedChoice === null || !current) return;
    setAnswered(true);
    const isCorrect = current.choices[selectedChoice] === current.answer;
    const newResults = [
      ...results,
      isCorrect ? ('correct' as const) : ('incorrect' as const),
    ];
    setResults(newResults);
    if (isCorrect) {
      doRecordCorrect(current.id);
    } else {
      doRecordIncorrect(current.id);
    }
  }, [selectedChoice, current, results, doRecordCorrect, doRecordIncorrect]);

  const handleNext = useCallback(() => {
    if (currentIndex + 1 >= total) {
      if (onFinished) onFinished();
      setFinished(true);
    } else {
      setCurrentIndex((prev) => prev + 1);
      setSelectedChoice(null);
      setAnswered(false);
    }
  }, [currentIndex, total, onFinished]);

  const handleClose = () => {
    // 閉じるときはセッションを保持（途中から再開できるように）
    onClose();
  };

  const handleRetryIncorrect = () => {
    if (onRetryIncorrect && incorrectQuestions.length > 0) {
      clearSession(); // 再挑戦時はセッションをクリア
      onRetryIncorrect(incorrectQuestions);
    }
  };

  // 復元確認プロンプト
  if (showResumePrompt && savedSession) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">途中から再開しますか？</h3>
              <p className="text-xs text-gray-500">前回の続きから始められます</p>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">進捗状況</span>
              <span className="text-sm font-bold text-teal-600">
                {savedSession.currentIndex + 1} / {total}問目
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-all"
                style={{ width: `${((savedSession.currentIndex + 1) / total) * 100}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs">
              <span className="flex items-center gap-1 text-teal-600">
                <i className="ri-checkbox-circle-fill"></i>
                正解: {savedSession.results.filter(r => r === 'correct').length}
              </span>
              <span className="flex items-center gap-1 text-red-500">
                <i className="ri-close-circle-fill"></i>
                不正解: {savedSession.results.filter(r => r === 'incorrect').length}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={restoreSession}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl text-sm font-bold hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-play-line text-base"></i>
              続きから再開する
            </button>
            <button
              onClick={startFromBeginning}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-restart-line text-base"></i>
              最初からやり直す
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Result screen
  if (finished) {
    const percentage = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    const isPerfect = percentage === 100;
    const isGreat = percentage >= 80;
    const isGood = percentage >= 60;

    const getMessage = () => {
      if (isPerfect)
        return {
          emoji: '🎉',
          title: 'パーフェクト！',
          sub: '全問正解です！素晴らしい！',
        };
      if (isGreat)
        return {
          emoji: '🏆',
          title: '素晴らしい！',
          sub: '高い正答率です！この調子で頑張りましょう！',
        };
      if (isGood)
        return {
          emoji: '💪',
          title: 'よくできました！',
          sub: 'もう少しで完璧です！復習を続けましょう！',
        };
      return {
        emoji: '📚',
        title: 'お疲れさまでした！',
        sub: '間違えた問題を復習して、もう一度挑戦しましょう！',
      };
    };

    const msg = getMessage();

    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={handleClose}
      >
        <div
          className="bg-white rounded-2xl max-w-md w-full max-h-[95vh] overflow-y-auto relative"
          onClick={(e) => e.stopPropagation()}
        >
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
              {Array.from({ length: 30 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full animate-confetti"
                  style={{
                    left: `${Math.random() * 100}%`,
                    backgroundColor: [
                      '#14b8a6',
                      '#f59e0b',
                      '#ef4444',
                      '#8b5cf6',
                      '#3b82f6',
                      '#ec4899',
                    ][i % 6],
                    animationDelay: `${Math.random() * 0.8}s`,
                    animationDuration: `${1.5 + Math.random() * 1.5}s`,
                  }}
                ></div>
              ))}
            </div>
          )}
          <div
            className={`relative px-5 sm:px-8 pt-8 sm:pt-10 pb-6 sm:pb-8 text-center ${
              isPerfect
                ? 'bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500'
                : isGreat
                ? 'bg-gradient-to-br from-teal-400 via-teal-500 to-emerald-600'
                : isGood
                ? 'bg-gradient-to-br from-teal-400 via-teal-500 to-emerald-500'
                : 'bg-gradient-to-br from-gray-500 to-gray-600'
            }`}
          >
            <div className="absolute inset-0 bg-white/5"></div>
            <div className="relative z-10">
              <div className="text-4xl sm:text-5xl mb-3 animate-bounce-slow">{msg.emoji}</div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 animate-fade-in-up">
                {msg.title}
              </h3>
              <p className="text-xs sm:text-sm text-white/80">{msg.sub}</p>
            </div>
          </div>
          <div className="relative -mt-8 flex justify-center z-20">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white shadow-xl flex flex-col items-center justify-center border-4 border-white">
              <svg className="absolute w-24 h-24 sm:w-28 sm:h-28" viewBox="0 0 112 112">
                <circle
                  cx="56"
                  cy="56"
                  r="48"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="6"
                />
                <circle
                  cx="56"
                  cy="56"
                  r="48"
                  fill="none"
                  stroke={isPerfect ? '#f59e0b' : 'url(#scoreGrad)'}
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 48}`}
                  strokeDashoffset={`${2 * Math.PI * 48 * (1 - percentage / 100)}`}
                  transform="rotate(-90 56 56)"
                  className="transition-all duration-1000"
                />
                <defs>
                  <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#14b8a6" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </svg>
              <span
                className={`text-2xl sm:text-3xl font-bold ${
                  isPerfect ? 'text-amber-500' : 'text-teal-600'
                }`}
              >
                {countUpValue}
              </span>
              <span className="text-xs text-gray-400 -mt-0.5">%</span>
            </div>
          </div>
          <div className="px-4 sm:px-8 pt-5 pb-5 sm:pb-6">
            <div className="flex justify-center gap-3 sm:gap-6 mb-5">
              <div className="text-center bg-teal-50 rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 flex-1">
                <div className="flex items-center justify-center gap-1 sm:gap-1.5 mb-1">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-sm sm:text-base"></i>
                  <span className="text-xs text-gray-500">正解</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-teal-600">{correctCount}</p>
              </div>
              <div className="text-center bg-red-50 rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 flex-1">
                <div className="flex items-center justify-center gap-1 sm:gap-1.5 mb-1">
                  <i className="ri-close-circle-fill text-red-400 text-sm sm:text-base"></i>
                  <span className="text-xs text-gray-500">不正解</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-red-500">{incorrectCount}</p>
              </div>
              <div className="text-center bg-gray-50 rounded-xl px-3 sm:px-5 py-2.5 sm:py-3 flex-1">
                <div className="flex items-center justify-center gap-1 sm:gap-1.5 mb-1">
                  <i className="ri-file-list-3-line text-gray-400 text-sm sm:text-base"></i>
                  <span className="text-xs text-gray-500">出題数</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-gray-700">{total}</p>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 mb-1.5 overflow-hidden flex">
              <div
                className="h-full bg-gradient-to-r from-teal-400 to-emerald-500 rounded-l-full transition-all duration-700"
                style={{ width: `${percentage}%` }}
              ></div>
              {incorrectCount > 0 && (
                <div
                  className="h-full bg-red-300 transition-all duration-700"
                  style={{ width: `${100 - percentage}%` }}
                ></div>
              )}
            </div>
            <div className="flex justify-between text-xs text-gray-400 mb-5 sm:mb-6">
              <span>正答率 {percentage}%</span>
              <span>
                {correctCount}/{total}問正解
              </span>
            </div>
            {incorrectQuestions.length > 0 && onRetryIncorrect && (
              <button
                onClick={handleRetryIncorrect}
                className="w-full py-3 mb-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-sm"
              >
                <i className="ri-refresh-line text-base"></i>
                間違えた{incorrectQuestions.length}問を再挑戦
              </button>
            )}
            <button
              onClick={handleClose}
              className={`w-full py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                incorrectQuestions.length > 0 && onRetryIncorrect
                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  : 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:from-teal-600 hover:to-emerald-700 shadow-sm'
              }`}
            >
              <i className="ri-arrow-left-line text-base"></i>
              クイズ一覧に戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={handleClose}
      >
        <div
          className="bg-gray-100 rounded-2xl max-w-md w-full p-5 sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-gray-700">質問がありません。</p>
          <button
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded cursor-pointer whitespace-nowrap"
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  const hasChoices = current.choices && current.choices.length === 4;
  const isCurrentBookmarked = bookmarkedQuestions.has(current.id);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={handleClose}
    >
      <div
        className="bg-gray-100 sm:rounded-2xl rounded-t-2xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gray-100 border-b border-gray-300 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10 sm:rounded-t-2xl">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
            <span className="px-2 sm:px-2.5 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full truncate max-w-[120px] sm:max-w-none">
              {current.subject}
            </span>
            {current.subcategory && (
              <span className="px-2 sm:px-2.5 py-0.5 bg-teal-50/60 text-teal-600 text-xs font-medium rounded-full truncate max-w-[100px] sm:max-w-none">
                {current.subcategory}
              </span>
            )}
            <span className="text-xs sm:text-sm text-gray-500 shrink-0">
              {currentIndex + 1} / {total}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2 relative">
            <button
              onClick={() => handleToggleBookmark(current.id)}
              className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isCurrentBookmarked
                  ? 'bg-amber-100 hover:bg-amber-200'
                  : 'hover:bg-gray-200'
              }`}
              title={isLoggedIn ? (isCurrentBookmarked ? '苦手マークを外す' : '苦手マークをつける') : 'ログインが必要です'}
            >
              <i className={`text-xl ${isCurrentBookmarked ? 'ri-bookmark-fill text-amber-500' : 'ri-bookmark-line text-gray-500'}`}></i>
            </button>
            {showLoginPrompt && (
              <div className="absolute top-full right-0 mt-2 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg z-20 animate-fade-in">
                <i className="ri-lock-line mr-1"></i>
                苦手チェックにはログインが必要です
              </div>
            )}
            <button
              onClick={handleClose}
              className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
          </div>
        </div>
        <div className="w-full bg-gray-200 h-1">
          <div
            className="h-1 bg-gradient-to-r from-teal-400 to-emerald-500 transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / total) * 100}%` }}
          ></div>
        </div>
        <div className="p-4 sm:p-6">
          <div className="mb-5 sm:mb-6">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full shrink-0">
                <span className="text-xs font-bold text-white">Q</span>
              </div>
              <span className="text-sm font-semibold text-gray-700">問題</span>
            </div>
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed whitespace-pre-wrap pl-8 sm:pl-9">
              {current.question}
            </p>
          </div>
          {hasChoices ? (
            <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-5">
              {current.choices.map((choice, idx) => {
                const isCorrectChoice = choice === current.answer;
                const isSelected = selectedChoice === idx;
                let choiceStyle =
                  'bg-white border-gray-300 hover:border-teal-400 hover:bg-teal-50 cursor-pointer';
                if (isSelected && !answered)
                  choiceStyle =
                    'bg-teal-50 border-teal-500 ring-2 ring-teal-200 cursor-pointer';
                if (answered) {
                  if (isCorrectChoice)
                    choiceStyle =
                      'bg-teal-50 border-teal-500 ring-2 ring-teal-200';
                  else if (isSelected && !isCorrectChoice)
                    choiceStyle =
                      'bg-red-50 border-red-400 ring-2 ring-red-200';
                  else
                    choiceStyle =
                      'bg-gray-50 border-gray-200 opacity-60';
                }
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelectChoice(idx)}
                    disabled={answered}
                    className={`w-full flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border rounded-xl text-left transition-all duration-200 ${choiceStyle} ${
                      answered ? 'cursor-default' : ''
                    }`}
                  >
                    <span
                      className={`w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                        answered && isCorrectChoice
                          ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white'
                          : answered && isSelected && !isCorrectChoice
                          ? 'bg-red-500 text-white'
                          : isSelected && !answered
                          ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {choiceLabels[idx]}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-800 flex-1">{choice}</span>
                    {answered && isCorrectChoice && (
                      <i className="ri-checkbox-circle-fill text-teal-600 text-base sm:text-lg shrink-0"></i>
                    )}
                    {answered && isSelected && !isCorrectChoice && (
                      <i className="ri-close-circle-fill text-red-500 text-base sm:text-lg shrink-0"></i>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="mb-4 sm:mb-5">
              {!answered ? (
                <button
                  onClick={() => {
                    setAnswered(true);
                    setResults([...results, 'correct']);
                    doRecordCorrect(current.id);
                  }}
                  className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-eye-line mr-2"></i>解答を表示する
                </button>
              ) : (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full shrink-0">
                      <span className="text-xs font-bold text-white">A</span>
                    </div>
                    <span className="text-sm font-semibold text-teal-700">
                      解答
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap pl-8 sm:pl-9">
                    {current.answer}
                  </p>
                </div>
              )}
            </div>
          )}
          {hasChoices && !answered && (
            <button
              onClick={handleConfirm}
              disabled={selectedChoice === null}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              回答する
            </button>
          )}
          {answered && hasChoices && (
            <div className="mb-3 sm:mb-4">
              {current.choices[selectedChoice!] === current.answer ? (
                <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-teal-50 border border-teal-200 rounded-xl">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-lg sm:text-xl"></i>
                  <span className="text-sm font-medium text-teal-700">正解！</span>
                </div>
              ) : (
                <div className="flex items-start gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-red-50 border border-red-200 rounded-xl">
                  <i className="ri-close-circle-fill text-red-500 text-lg sm:text-xl shrink-0 mt-0.5"></i>
                  <span className="text-xs sm:text-sm font-medium text-red-600">
                    不正解… 正解は{' '}
                    {choiceLabels[current.choices.indexOf(current.answer)]}.{' '}
                    {current.answer}
                  </span>
                </div>
              )}
            </div>
          )}
          {answered && current.explanation && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-amber-500 rounded-full shrink-0">
                  <i className="ri-lightbulb-line text-xs text-white"></i>
                </div>
                <span className="text-sm font-semibold text-amber-700">
                  解説
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap pl-8 sm:pl-9">
                {current.explanation}
              </p>
            </div>
          )}
          {answered && (
            <button
              onClick={handleNext}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap"
            >
              {currentIndex + 1 >= total ? '結果を見る' : '次の問題へ'}
              <i className="ri-arrow-right-line ml-1.5"></i>
            </button>
          )}
        </div>
        <div className="border-t border-gray-300 px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-center gap-4 sm:gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <i className="ri-checkbox-circle-line text-teal-500"></i>
            正解: {correctCount}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-close-circle-line text-red-400"></i>
            不正解: {incorrectCount}
          </span>
        </div>
      </div>
    </div>
  );
}